import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { ClipboardList, Truck, Cog, FileCheck, ArrowRight, LucideIcon } from 'lucide-react';
import { useJsonData } from '../../hooks/useJsonData';
import { useSafeAnimation, useSafeVariants } from '../ui/SafeMotion';

// Icon mapping
const iconMap: Record<string, LucideIcon> = {
  ClipboardList, Truck, Cog, FileCheck
};

interface Step {
  icon: string;
  number: string;
  title: string;
  description: string;
  color: string;
}

interface HowItWorksData {
  steps: Step[];
}

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

export function HowItWorks() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { data, loading } = useJsonData<HowItWorksData>('/data/how-it-works.json');

  // SSR-safe animation props
  const headerAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 },
    isInView
  );
  const badgeAnim = useSafeAnimation(
    { opacity: 0, scale: 0.9 },
    { opacity: 1, scale: 1 },
    isInView
  );
  const variants = useSafeVariants(isInView);

  const steps = data?.steps || [];

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-gradient-to-b from-eco-green-50 to-white overflow-hidden relative">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-eco-green-100 rounded-full blur-3xl opacity-50 -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-eco-green-100 rounded-full blur-3xl opacity-50 translate-x-1/2 translate-y-1/2" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={headerAnim.initial}
          animate={headerAnim.animate}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={badgeAnim.initial}
            animate={badgeAnim.animate}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-eco-green-200 text-eco-green-700 text-sm font-semibold rounded-full mb-6 shadow-sm"
          >
            <Cog className="w-4 h-4" />
            How Eco Enviro Works
          </motion.span>
          <h2 className="text-4xl sm:text-5xl font-bold text-eco-green-900 mb-4">
            Simple. Structured. Compliant.
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto text-lg">
            Our streamlined process ensures maximum recovery with full compliance.
          </p>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-48 bg-gray-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        )}

        {/* Steps */}
        {!loading && (
          <motion.div
            variants={containerVariants}
            initial={variants.initial}
            animate={variants.animate}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4"
          >
            {steps.map((step, index) => {
              const IconComponent = iconMap[step.icon] || Cog;
              return (
                <motion.div
                  key={step.title}
                  variants={itemVariants}
                  className="relative"
                >
                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-2 w-4 z-10">
                      <ArrowRight className="w-4 h-4 text-eco-green-300" />
                    </div>
                  )}

                  <motion.div
                    whileHover={{ y: -8, scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-2xl p-6 text-center h-full shadow-lg shadow-eco-green-100/50 border border-eco-green-100 hover:border-eco-green-300 hover:shadow-xl hover:shadow-eco-green-200/50 transition-all"
                  >
                    {/* Number Badge */}
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className={`inline-block px-4 py-1 bg-gradient-to-r ${step.color} text-white text-xs font-bold rounded-full shadow-lg`}>
                        Step {step.number}
                      </span>
                    </div>

                    {/* Icon */}
                    <div className={`w-16 h-16 mx-auto mb-4 mt-2 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center shadow-lg`}>
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>

                    <h3 className="text-xl font-bold text-eco-green-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {step.description}
                    </p>
                  </motion.div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </section>
  );
}
